/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {auditBy, currentTimeMillies, Day, generateHex, Hour, Module, randomNumber} from "@intuitionrobotics/ts-common";
import {AlertStatus, AlertType, DB_Alert} from "@app/app-shared/alert";
import {DayFormat, DB_PatientData, TableKeys} from "@app/app-shared/patient-data";
import {ENERGY_LEVELS_CONFIG_KEY, GENERAL_WELL_BEING_CONFIG_KEY, getInitialConfigForOrganization, PAIN_LEVELS_CONFIG_KEY, timeUnits} from "@app/app-shared/care_config";
import {GroupType} from "@app/ir-q-app-common/types/contacts";
import dayjs from "../app/utils/dayjs";
import {MessageStatuses} from "@app/ir-q-app-common/types/message";
import {DB_Patient} from "@app/app-shared/patient";
import {DB_Organization, ORGANIZATION_TYPE} from "@app/ir-q-app-common/types/care-center/organization";
import {DB_Contact, Relation} from "@app/ir-q-app-common/types/db-contact";
import {Elliq_ProductKey} from "@app/ir-q-app-common/shared/consts";
import {CareGoalsMap, UnitGoalMap} from "@modules/GoalsConfigModule";
import {windowType} from "@app/app-shared/api";
import {OrganizationMap} from "../app/pages/ui/organizations/OrganizationsContext";
import {ContactsMap} from "../app/pages/ui/messages/ContactsContext";
import {ChatsMap} from "../app/pages/ui/messages/ChatsContext";
import {MessageWithStatus} from "@app/ir-q-app-common/types/message-api";
import {CalendarEventType, ScheduledBy} from "@app/ir-q-app-common/types/calendar_event";
import {Reminder} from "@app/app-shared/reminder";
import {Config, names, uniqueNamesGenerator} from "unique-names-generator";
import {getUserTokenInfo} from "@modules/utils";
import {PatientGender} from "@app/ir-q-app-common/types/care-center/user";
import {PatientDataMap} from "@modules/PatientDataModule";

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const getRandomLastWeek = () => Math.ceil(Math.random() * 7 * Day)

export class DemoData_Class
    extends Module {

    constructor() {
        super("DemoData");
    }

    //
    // __onDemoToggle(on: boolean) {
    //     if (!on)
    //         return this.resetDemoData();
    //
    //     const demoPatients = DemoModule.getDemoPatients();
    //     if (!demoPatients)
    //         return;
    //
    //     demoPatients.forEach(_patient => {
    //         this.alert_data.push(
    //             this.generateAlertContactRequest(_patient._id),
    //             this.generateAlertFeelingWorse(_patient._id),
    //             this.generateAlertPhq2Results(_patient._id)
    //         );
    //         this.buildPatientData(_patient.unitId);
    //     });
    // }
    //
    // private resetDemoData = () => {
    //     this.alert_data = [];
    //     this.dashboard_data = {};
    // };
    //
    // alert_data: DB_Alert<any>[] = [];
    // dashboard_data: PatientDataMap = {};

    public generateNewAlertsForPatients = (patientId: string) => [
        this.generateAlertContactRequest(patientId),
        this.generateAlertAbnormalBloodPressure(patientId),
        this.generateAlertFeelingWorse(patientId),
        this.generateAlertPhq2Results(patientId)
    ];

    private generateAlertContactRequest = (patientId: string) => {
        const t = getRandomLastWeek()
        return ({
            _id: `${patientId}_contact_me`,
            type: AlertType.PatientContactRequest,
            status: AlertStatus.OPEN,
            subject: "Contact me",
            details: "Not feeling well",
            category: GroupType.CLINIC,
            _created: {auditBy: "fe-stub", auditAt: {timestamp: currentTimeMillies() - t, timezone: timezone, pretty: ""}},
            _updated: {auditBy: "fe-stub", auditAt: {timestamp: currentTimeMillies() - t + Day, timezone: timezone, pretty: ""}},
            patientId,
            sectionName: "",
            alertTrigger: "",
            alertType: "contact_me"
        });
    };

    private generateAlertAbnormalBloodPressure = (patientId: string) => {
        const t = getRandomLastWeek();
        return ({
            _id: `${patientId}_abnormal_blood_pressure`,
            type: AlertType.ElliQInitiated,
            status: AlertStatus.COMPLETE,
            subject: "Abnormal blood pressure",
            details: "Patient reported measuring systolic 175.0 mmHg",
            category: GroupType.CLINIC,
            _created: {auditBy: "fe-stub", auditAt: {timestamp: currentTimeMillies() - t, timezone: timezone, pretty: ""}},
            _updated: {auditBy: "fe-stub", auditAt: {timestamp: currentTimeMillies() - t + Day, timezone: timezone, pretty: ""}},
            patientId,
            sectionName: "",
            alertTrigger: "",
            alertType: "health_measurement_alert",
            feedback: {clinical_action: ["care_plan_updated_or_new_instructions_to_patient"]},
            feedbackAudit: auditBy(`Dr. `)
        });
    };
    private generateAlertFeelingWorse = (patientId: string) => {
        const t = getRandomLastWeek();
        return ({
            _id: `${patientId}_feeling_worse`,
            type: AlertType.ElliQInitiated,
            status: AlertStatus.COMPLETE,
            subject: "Feeling worse",
            details: "",
            category: GroupType.CLINIC,
            _created: {auditBy: "fe-stub", auditAt: {timestamp: currentTimeMillies() - t, timezone: timezone, pretty: ""}},
            _updated: {auditBy: "fe-stub", auditAt: {timestamp: currentTimeMillies() - t + Day, timezone: timezone, pretty: ""}},
            patientId,
            sectionName: "",
            alertTrigger: "",
            alertType: "contact_me",
            feedback: {clinical_action: ["care_plan_updated_or_new_instructions_to_patient"]},
            feedbackAudit: auditBy(`Dr. `)
        });
    };

    private generateAlertPhq2Results = (patientId: string): DB_Alert<any> => {
        const t = getRandomLastWeek()
        return ({
            _id: `${patientId}_phq2`,
            type: AlertType.ElliQInitiated,
            status: AlertStatus.COMPLETE,
            subject: "New PHQ-2 score collected",
            description: "Customer PHQ-2 Score is 6",
            details: "Customer PHQ-2 Score is 6",
            category: GroupType.CLINIC,
            _created: {auditBy: "fe-stub", auditAt: {timestamp: currentTimeMillies() - t, timezone: timezone, pretty: ""}},
            _updated: {auditBy: "fe-stub", auditAt: {timestamp: currentTimeMillies() - t + Day, timezone: timezone, pretty: ""}},
            patientId,
            alertTrigger: "survey_metroPlus01",
            sectionName: "",
            alertType: "phq_2"
        });
    };

    private generateCheckInData = (unitId: string) => {
        const pain = [1, 3, 3, 1, 3, 1, 1, 3, 1, 3, 3, 1, 3, 1, 1, 3, 3, 1, 3, 1, 1, 1, 3, 1, 3, 3, 1, 3];
        const energy = [1, 3, 1, 5, 1, 5, 3, 5, 5, 1, 1, 3, 3, 1, 1, 1, 1, 1, 3, 1, 3, 1, 5, 5, 5, 5, 3];
        const wellbeing = [5, 3, 3, 5, 5, 5, 3, 5, 5, 3, 3, 3, 1, 3, 1, 3, 3, 1, 1, 1, 3, 5, 5, 3, 3, 3, 1, 3];
        const patientDataArray: DB_PatientData[] = [];
        const now = dayjs().startOf("day");
        for (let idx = pain.length - 1; idx >= 0; idx--) {
            const ts = this.realTimeStamp(now.clone().subtract(pain.length - idx, "day"));
            patientDataArray.push({
                _id: generateHex(8),
                unitId,
                timestamp: ts.valueOf(),
                date: ts.format(DayFormat),
                label: PAIN_LEVELS_CONFIG_KEY,
                value: pain[idx],
                key: TableKeys.CheckIn
            });
            patientDataArray.push({
                _id: generateHex(8),
                unitId,
                timestamp: ts.valueOf(),
                date: ts.format(DayFormat),
                label: ENERGY_LEVELS_CONFIG_KEY,
                value: energy[idx],
                key: TableKeys.CheckIn
            });

            patientDataArray.push({
                _id: generateHex(8),
                unitId,
                timestamp: ts.valueOf(),
                date: ts.format(DayFormat),
                label: GENERAL_WELL_BEING_CONFIG_KEY,
                value: wellbeing[idx],
                key: TableKeys.CheckIn
            });

        }
        return patientDataArray;
    };

    private generateMoodData = (unitId: string): DB_PatientData[] => {

        const mood = [5, 3, 1, 3, 5, 5, 5, 1, 5, 3, 5, 3, 1, 3, 1, 1, 5, 1, 1, 1];
        const patientDataArray: DB_PatientData[] = [];
        const now = dayjs().startOf("day");
        for (let idx = mood.length - 1; idx >= 0; idx--) {
            const ts = this.realTimeStamp(now.clone().subtract(mood.length - idx, "day"));
            patientDataArray.push({
                _id: generateHex(8),
                unitId,
                timestamp: ts.valueOf(),
                date: ts.format(DayFormat),
                label: "mood",
                value: mood[idx],
                key: TableKeys.Mood
            });
        }
        return patientDataArray;
    };

    private generatePainData = (unitId: string, repeatCount = 1): DB_PatientData[] => {

        const painData = [5, 3, 1, 3, 5, 8, 5, 1, 5, 3, 5, 3, 1, 3, 1, 1, 5, 1, 1, 1];
        const pain = [];
        // Double the data
        for (let i = 0; i < repeatCount; i++) {
            pain.push(...painData);
        }
        const patientDataArray: DB_PatientData[] = [];
        const now = dayjs().startOf("day");
        for (let idx = pain.length - 1; idx >= 0; idx--) {
            const ts = now.clone().subtract(pain.length - idx, "day");
            patientDataArray.push({
                _id: generateHex(8),
                unitId,
                label: PAIN_LEVELS_CONFIG_KEY,
                date: ts.format(DayFormat),
                timestamp: ts.valueOf(),
                value: pain[idx],
                key: TableKeys.Pain,
                extraData: {
                    bodyPart: "shoulder",
                    feelPain: true,
                    painLevel: pain[idx],
                    newPain: pain[idx] % 2 === 0
                }
            });
        }
        return patientDataArray;
    };

    private generateSleepData = (unitId: string): DB_PatientData[] => {
        const sleep = [5, 5, 5, 1, 5, 5, 1, 5, 5, 3, 5, 1, 1, 3, 3, 1, 5, 1, 1, 1];
        const patientDataArray: DB_PatientData[] = [];
        const now = dayjs().startOf("day");
        for (let idx = sleep.length - 1; idx >= 0; idx--) {
            const ts = this.realTimeStamp(now.clone().subtract(sleep.length - idx, "day"));
            patientDataArray.push({
                _id: generateHex(8),
                unitId,
                timestamp: ts.valueOf(),
                date: ts.format(DayFormat),
                label: "sleep",
                value: sleep[idx],
                key: TableKeys.Sleep
            });
        }
        return patientDataArray;
    };


    generateSelfReportData = (label: string, tableKey: TableKeys.Sleep | TableKeys.Mood | TableKeys.Stress | TableKeys.Exercise, dayCount: number, unitId: string, paramValue?: number): DB_PatientData[] => {
        const patientDataArray: DB_PatientData[] = [];

        let currDate = new Date(currentTimeMillies()).setHours(0, 0, 0);
        for (let i = 0; i < dayCount; i++) {
            const value = Math.floor(Math.random() * 3);
            const possibleValues = currDate >= currentTimeMillies() - (15 * Day) ? [3, 5, 5] : [1, 1, 3];
            const skipToday = Math.floor(Math.random() * 6);
            if (skipToday !== 1) patientDataArray.push({
                _id: generateHex(8),
                unitId,
                timestamp: currDate,
                date: dayjs(currDate).format(DayFormat),
                label: label,
                value: currDate > currentTimeMillies() - (3 * Day) ? 1 : paramValue || possibleValues[value],
                key: tableKey
            });
            currDate = currDate - Day;
        }
        return patientDataArray;
    };

    generateBarGraphData = (label: string, tableKey: TableKeys.SelfReport, dayCount: number, unitId: string, average: number, std: number, frequency: number) => {
        const patientDataArray: DB_PatientData[] = [];

        let currDate = currentTimeMillies();
        for (let i = 0; i < dayCount; i++) {
            const timestamp = (currDate + Math.floor(Math.random() * Day) - Day);
            patientDataArray.push({
                _id: generateHex(8),
                unitId,
                timestamp,
                date: dayjs(currDate).format(DayFormat),
                label: label,
                value: Math.floor(Math.random() * std) + average,
                key: tableKey
            });
            if (label === "systolic") {
                patientDataArray.push({
                    _id: generateHex(8),
                    unitId,
                    timestamp,
                    date: dayjs(currDate).format(DayFormat),
                    label: "diastolic",
                    value: Math.floor(Math.random() * 10) + 80,
                    key: tableKey
                });

                patientDataArray.push({
                    _id: generateHex(8),
                    unitId,
                    timestamp,
                    date: dayjs(currDate).format(DayFormat),
                    label: "heart_rate",
                    value: Math.floor(Math.random() * 10) + 70,
                    key: tableKey
                });
            }
            currDate = currDate - (Day * frequency);
        }
        return patientDataArray;
    };

    private realTimeStamp = (momentObj: dayjs.Dayjs): dayjs.Dayjs => {
        return momentObj.add(8, "h").add(randomNumber(8 * Hour), "ms");
    };

    private generateFakeBpData = (unitId: string) => {
        const patientDataArray: DB_PatientData[] = [];
        const bp = [
            {systolic: 167, diastolic: 70},
            {systolic: 168, diastolic: 71},
            {systolic: 164, diastolic: 72},
            {systolic: 169, diastolic: 70},
            {systolic: 168, diastolic: 73},
            {systolic: 167, diastolic: 70},
            {systolic: 165, diastolic: 69},
            {systolic: 168, diastolic: 70},
            {systolic: 164, diastolic: 71},
            {systolic: 169, diastolic: 72},
            {systolic: 170, diastolic: 70},
            {systolic: 172, diastolic: 73},
            {systolic: 174, diastolic: 70},
            {systolic: 173, diastolic: 70},
            {systolic: 174, diastolic: 72},
            {systolic: 175, diastolic: 74},
            {systolic: 177, diastolic: 73},
            {systolic: 178, diastolic: 75},
            {systolic: 140, diastolic: 73}
        ];

        const weight = [170, 190, 189, 190, 190, 192, 190, 190, 193, 190, 190, 191, 190, 190, 190, 190, 190, 190, 190, 190, 190];
        const now = dayjs().startOf("day");
        for (let idx = bp.length - 1; idx >= 0; idx--) {
            const dato = bp[idx];
            const ts = this.realTimeStamp(now.clone().subtract(bp.length - idx, "day"));
            patientDataArray.push({
                _id: generateHex(8),
                unitId,
                timestamp: ts.valueOf(),
                date: ts.format(DayFormat),
                label: "systolic",
                value: dato.systolic,
                key: TableKeys.SelfReport
            });
            patientDataArray.push({
                _id: generateHex(8),
                unitId,
                timestamp: ts.valueOf(),
                date: ts.format(DayFormat),
                label: "diastolic",
                value: dato.diastolic,
                key: TableKeys.SelfReport
            });

            patientDataArray.push({
                _id: generateHex(8),
                unitId,
                timestamp: ts.valueOf(),
                date: ts.format(DayFormat),
                label: "heart_rate",
                value: 90 + ((Math.random() - 0.5) * 20),
                key: TableKeys.SelfReport
            });

            if (weight[idx - 1]) {
                patientDataArray.push({
                    _id: generateHex(8),
                    unitId,
                    timestamp: ts.valueOf(),
                    date: ts.format(DayFormat),
                    label: "weight",
                    value: weight[idx - 1],
                    key: TableKeys.SelfReport
                });
            }
        }
        return patientDataArray;
    };

    phqData = (unitId: string) => {
        const n = 10;
        return new Array(n).fill(0).flatMap((_, idx): DB_PatientData[] => {
            const timestamp = dayjs().subtract(idx, "month").startOf("month").add(Math.ceil(Math.random() * 30), "day").valueOf();
            const planId = generateHex(8);
            const generate = (sectionName: string) => {
                const value = idx === n - 1 ? 3 : Math.ceil(Math.random() * 3);
                return {
                    _id: `depression_assessment_${unitId}_${timestamp}_${sectionName}_${idx}`,
                    unitId,
                    timestamp: timestamp,
                    date: dayjs(timestamp).format(DayFormat),
                    label: "depression_assessment",
                    value,
                    key: TableKeys.PsychologicalAssessment,
                    extraData: {
                        sectionName,
                        planId,
                        unitId,
                        timestamp: timestamp
                    }
                }
            }
            return [generate("feeling_down_frequency"), generate("little_interest_frequency")]
        })
    };

    generateLastMessageForPatient = ({contactId}: DB_Patient, {groupContactIds}: DB_Organization) => {
        const unreadCount = Math.floor(Math.random() * 10);
        const chatId = generateHex(24);
        const messageId = generateHex(24);
        const senderId = unreadCount > 0 ? contactId : groupContactIds[0];
        const contactIds = [groupContactIds[0], contactId];
        const messageTimestamp = currentTimeMillies() - Math.floor(Math.random() * 1000 * 60 * 60 * 24);
        const lastMessage: MessageWithStatus = {
            senderId,
            chatId,
            data: {
                mimeType: "audio/mp3",
                text: "Hi, this is a devoted customer, I wanted you to know ElliQ is truly amazing and helpful",
                bucketPointer: {
                    bucketName: "",
                    path: ""
                },
                cacheId: generateHex(24)
            },
            draft: false,
            _id: messageId,
            type: "text",
            timestamp: messageTimestamp,
            status: {
                chatId,
                messageId,
                recipientId: contactIds.find(c => c !== senderId)!,
                _id: generateHex(24),
                messageTimestamp,
                status: MessageStatuses.RECEIVED,
                timestamp: messageTimestamp + 1000000
            }
        };
        return {unreadCount, chatId, contactIds, lastMessage};
    }

    getCsContact = (contactId: string, {name}: DB_Organization): DB_Contact<'group'> => ({
        _id: contactId,
        firstName: "Customer",
        lastName: "Service",
        middleName: name,
        contactType: "group",
        contactData: {
            type: GroupType.CUSTOMER_SERVICE
        },
        connections: [],
        __connectionIds: [],
        _created: auditBy("demo"),
        _updated: auditBy("demo")
    })

    getClinicalContact = (contactId: string, {name}: DB_Organization): DB_Contact<'group'> => ({
        _id: contactId,
        firstName: name,
        lastName: "",
        middleName: GroupType.CLINIC,
        contactType: "group",
        contactData: {
            type: GroupType.CLINIC
        },
        connections: [],
        __connectionIds: [],
        _created: auditBy("demo"),
        _updated: auditBy("demo")
    });

    getExternalContact = (contactId: string, {name}: DB_Organization): DB_Contact<'group'> => ({
        _id: contactId,
        firstName: name,
        lastName: "",
        middleName: GroupType.EXTERNAL,
        contactType: "group",
        contactData: {
            type: GroupType.EXTERNAL
        },
        connections: [],
        __connectionIds: [],
        _created: auditBy("demo"),
        _updated: auditBy("demo")
    })

    getPatientContact = ({contactId, name, unitId}: DB_Patient): DB_Contact<'agentUser'> => ({
        _id: contactId,
        firstName: name,
        lastName: "",
        contactType: "agentUser",
        contactData: {
            unitId: unitId,
            product: Elliq_ProductKey
        },
        connections: [],
        __connectionIds: [],
        _created: auditBy("demo"),
        _updated: auditBy("demo")
    })

    getFakeGoals(generateDemoPatients: DB_Patient[], orgs?: { [key: string]: DB_Organization }) {
        return generateDemoPatients.reduce((acc: CareGoalsMap, {unitId, organizationId}) => {
            const organization = orgs?.[organizationId];
            if (!organization)
                return acc;
            const initialConfigForOrganization = getInitialConfigForOrganization(organization);
            return {
                ...acc,
                [unitId]: Object.entries(initialConfigForOrganization).reduce((unitAcc: UnitGoalMap, [k, c]) => {
                    if (!c)
                        return unitAcc;

                    return {
                        ...unitAcc,
                        [k]: {
                            name: k,
                            windowType: windowType[timeUnits.indexOf(c.frequency.value)],
                            threshold: c.frequency.times,
                            ranges: {}
                        }
                    };
                }, {})

            }
        }, {});
    }

    getFakeContacts = (generateDemoPatients: DB_Patient[], orgs?: OrganizationMap) => {
        const orgContacts = Object.values(orgs || {}).reduce((acc: ContactsMap, org) => {
            const [cs, cl, ex] = org.groupContactIds;
            acc[cs] = DemoDataUtils.getCsContact(cs, org);
            acc[cl] = DemoDataUtils.getClinicalContact(cl, org);
            acc[ex] = DemoDataUtils.getExternalContact(ex, org);
            return acc;
        }, {})
        return generateDemoPatients.reduce((acc: ContactsMap, p) => {
            const org = orgs?.[p.organizationId]
            if (!org)
                return acc;

            const patientContact = DemoDataUtils.getPatientContact(p);
            const connect = (c1: DB_Contact<"agentUser">, c2: DB_Contact<any>) => {
                c1.connections.push({
                    _id: c2._id,
                    relation: Relation.GROUP
                });
                c2.connections.push({
                    _id: c1._id,
                    relation: Relation.AGENT_USER
                });
                c1.__connectionIds.push(c2._id);
                c2.__connectionIds.push(c1._id);
            }
            const [cs, cl, ex] = org.groupContactIds;
            const csContact = acc[cs];
            const clContact = acc[cl];
            const exContact = acc[ex];
            if (csContact)
                connect(patientContact, csContact);
            switch (org.type) {
                case ORGANIZATION_TYPE.INTERNAL:
                    break;
                case ORGANIZATION_TYPE.CLINICAL: {
                    if (clContact)
                        connect(patientContact, clContact);
                    break;
                }
                case ORGANIZATION_TYPE.EXTERNAL: {
                    if (exContact)
                        connect(patientContact, exContact);
                    break;
                }
                case ORGANIZATION_TYPE.MULTI_CONTACT: {
                    if (exContact)
                        connect(patientContact, exContact);
                    if (clContact)
                        connect(patientContact, clContact);
                }
            }

            acc[patientContact._id] = patientContact;
            return acc
        }, orgContacts);
    };

    getFakeChats = (generateDemoPatients: DB_Patient[], orgs?: OrganizationMap) => {
        return generateDemoPatients.reduce((acc: { chats: ChatsMap, messagesToAdd: MessageWithStatus[] }, p) => {
            const org = orgs?.[p.organizationId]
            if (!org)
                return acc;
            const {unreadCount, chatId, contactIds, lastMessage} = DemoDataUtils.generateLastMessageForPatient(p, org);
            return {
                chats: {
                    ...acc.chats,
                    [chatId]: {
                        _updated: auditBy("demo"),
                        _id: chatId,
                        contactIds,
                        unreadMessages: [],
                        unreadCount: unreadCount,
                        lastMessage
                    }
                },
                messagesToAdd: [...acc.messagesToAdd, lastMessage]
            };
        }, {chats: {}, messagesToAdd: []});
    }

    getEvents = (unitId: string, org: DB_Organization): Reminder[] => {
        const subjects = [
            "Take pills",
            "Take medication",
            "Take your pills",
            "Take your medication",
            "Take advil",
            "Take your advil"
        ]
        const frequencies = [
            "FREQ=WEEKLY;BYDAY=MO,WE,FR",
            "FREQ=WEEKLY;BYDAY=TU,TH,SA",
            "FREQ=WEEKLY;BYDAY=MO,WE,FR,SA",
            "FREQ=WEEKLY;BYDAY=TU,TH,SA,SU",
            "FREQ=WEEKLY;BYDAY=MO,WE,FR,SU",
            "FREQ=WEEKLY;BYDAY=TU,TH,SA,SU",
            "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR,SA,SU",
            "",
            "",
            ""
        ]
        const contactId = org.type === ORGANIZATION_TYPE.CLINICAL ? org.groupContactIds[1] : org.groupContactIds[0];
        return new Array(10).fill(0).map((_, idx) => {
            const startTime = dayjs().subtract(Math.ceil(Math.random() * 10), "day").valueOf();
            const endTime = dayjs().add(Math.ceil(Math.random() * 10), "day").valueOf();
            const subject = subjects[Math.floor(Math.random() * subjects.length)];
            return {
                id: randomNumber(100000),
                subject: subject,
                description: "reminders",
                startTime,
                endTime,
                eventType: CalendarEventType.REMINDER,
                unitId,
                originator: ScheduledBy.USER,
                reminderCount: 0,
                status: "CREATED",
                recurring: frequencies[Math.floor(Math.random() * frequencies.length)],
                updateTs: startTime,
                createTs: startTime,
                metadata: {
                    contactId: contactId,
                    contactName: org.name,
                    reminderType: CalendarEventType.SUGGESTED_MEDICATION_REMINDER,
                    medsMealRequired: false,
                    isCriticalMedication: false,
                    sharedReminderInstance: generateHex(16)
                }
            }
        })
    }

    getPatientReminders = (generateDemoPatients: DB_Patient[], orgs?: OrganizationMap) => generateDemoPatients.reduce((acc: { [patientId: string]: Reminder[] }, p) => {
        const org = orgs?.[p.organizationId];
        if (!org)
            return acc;
                const subjects = [
                    "Take pills",
                    "Take medication",
                    "Take your pills",
                    "Take your medication",
                    "Take advil",
                    "Take your advil"
                ]
                const frequencies = [
                    "FREQ=WEEKLY;BYDAY=MO,WE,FR",
                    "FREQ=WEEKLY;BYDAY=TU,TH,SA",
                    "FREQ=WEEKLY;BYDAY=MO,WE,FR,SA",
                    "FREQ=WEEKLY;BYDAY=TU,TH,SA,SU",
                    "FREQ=WEEKLY;BYDAY=MO,WE,FR,SU",
                    "FREQ=WEEKLY;BYDAY=TU,TH,SA,SU",
                    "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR,SA,SU",
                    "",
                    "",
                    ""
                ]

        return {...acc, [p._id]: new Array(10).fill(0).map((): Reminder => {
                const startTime = dayjs().subtract(Math.ceil(Math.random() * 10), "day").valueOf();
                const endTime = dayjs().add(Math.ceil(Math.random() * 10), "day").valueOf();
                const subject = subjects[Math.floor(Math.random() * subjects.length)];
                return {
                    id: randomNumber(100000),
                    subject: subject,
                    description: "reminders",
                    startTime,
                    endTime,
                    eventType: CalendarEventType.REMINDER,
                    unitId: p.unitId,
                    originator: ScheduledBy.USER,
                    reminderCount: 0,
                    status: "CREATED",
                    recurring: frequencies[Math.floor(Math.random() * frequencies.length)],
                    updateTs: startTime,
                    createTs: startTime,
                    metadata: {
                        contactId: org.type === ORGANIZATION_TYPE.CLINICAL ? org.groupContactIds[1] : org.groupContactIds[0],
                        contactName: org.name,
                        reminderType: CalendarEventType.SUGGESTED_MEDICATION_REMINDER,
                        medsMealRequired: false,
                        isCriticalMedication: false,
                        sharedReminderInstance: generateHex(16)
                    }
                }
            })
        }
    }, {});


    generateDemoPatients = (amount: number, orgIds: string[]) => {
        const customConfig: Config = {
            dictionaries: [names, names],
            separator: ' ',
            style: 'capital',
            length: 2
        };
        const lastMonth = currentTimeMillies() - 30 * 24 * 60 * 60 * 1000;

        return new Array(amount).fill(0).map((_, idx): DB_Patient => {
            const ts = auditBy(getUserTokenInfo().account.email, undefined, lastMonth);
            const year = 365 * 24 * 60 * 60 * 1000;
            const name = uniqueNamesGenerator(customConfig);
            return {
                _id: generateHex(16),
                name,
                unitId: `ir-dev-${name.split(" ")[0].toLowerCase()}`,
                gender: Math.random() > 0.5 ? PatientGender.FEMALE : PatientGender.MALE,
                organizationId: orgIds[Math.floor(Math.random() * orgIds.length)],
                // Get the current user's timezone
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                _updated: ts,
                _created: ts,
                privacyAccepted: true,
                contactId: generateHex(16),
                _carePlanReviewed: ts,
                birthday: currentTimeMillies() - 65 * year - Math.floor(Math.random() * 15 * year),
                disabled: false
            };
        })
    }

    getPatientData = (generateDemoPatients: DB_Patient[]) => generateDemoPatients.reduce((allData: PatientDataMap, {unitId}) => ({
        ...allData,
        [unitId]: {
            [TableKeys.CheckIn]: this.generateCheckInData(unitId),
            [TableKeys.Pain]: this.generatePainData(unitId, 10),
            [TableKeys.Mood]: this.generateMoodData(unitId),
            [TableKeys.Sleep]: this.generateSleepData(unitId),
            [TableKeys.Stress]: this.generateSelfReportData("stress", TableKeys.Stress, 30, unitId, 1),
            [TableKeys.Exercise]: this.generateSelfReportData("exercise", TableKeys.Exercise, 30, unitId, 1),
            [TableKeys.SelfReport]: this.generateFakeBpData(unitId),
            [TableKeys.PsychologicalAssessment]: this.phqData(unitId)
        }
    }), {})}


export const DemoDataUtils = new DemoData_Class();
